import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Note: omitimos `size` do HTMLInputAttributes (que é numérico — largura em chars)
 * pra reusar a prop como variant 'sm' | 'md' | 'lg'.
 */
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Texto de ajuda abaixo do input */
  hint?: string;
  /** Mensagem de erro (sobrescreve hint quando presente) */
  error?: string;
  /** Ícone à esquerda dentro do input */
  leftIcon?: ReactNode;
  /** Ícone/botão à direita dentro do input */
  rightIcon?: ReactNode;
  /** Tamanho do input */
  size?: 'sm' | 'md' | 'lg';
  /**
   * UIUX-Comercial U1 (aditivo): classe do WRAPPER externo (default `w-full`).
   * Num flex-row, a largura precisa ir aqui — `className` no <input> interno
   * não impede o wrapper de ocupar a linha inteira.
   */
  wrapperClassName?: string;
}

const SIZE_CLASSES = {
  sm: 'h-9 text-sm',
  md: 'h-11 text-sm',
  lg: 'h-12 text-base',
};

/**
 * Input padrão do design system.
 *
 * Estados: default, focused (borda vermelha + halo), error (borda vermelha forte), disabled.
 *
 * Exemplos:
 *   <Input placeholder="seu@email.com" />
 *   <Input leftIcon={<SearchIcon />} placeholder="Buscar..." />
 *   <Input error="Email inválido" />
 *   <Input hint="Mínimo 8 caracteres" />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ hint, error, leftIcon, rightIcon, size = 'md', className, wrapperClassName, type = 'text', disabled, ...props }, ref) => {
    const hasError = !!error;
    const sizeCls = SIZE_CLASSES[size];

    return (
      <div className={wrapperClassName ?? 'w-full'}>
        <div className="relative">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            type={type}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            className={cn(
              'w-full rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1',
              'placeholder-ink-3 border border-line dark:border-line-dark',
              'focus:outline-none focus:ring-2 transition-colors',
              sizeCls,
              leftIcon ? 'pl-10' : 'px-4',
              rightIcon ? 'pr-10' : !leftIcon ? '' : 'pr-4',
              !leftIcon && !rightIcon ? '' : '',
              !leftIcon && 'px-4',
              hasError
                ? 'border-red focus:border-red focus:ring-red-soft dark:focus:ring-red-soft-dark'
                : 'focus:border-red focus:ring-red-soft dark:focus:ring-red-soft-dark',
              disabled && 'opacity-60 cursor-not-allowed',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3">
              {rightIcon}
            </span>
          )}
        </div>
        {(hint || error) && (
          <p className={cn('text-xs mt-1.5', hasError ? 'text-red' : 'text-ink-3 dark:text-ink-dark-3')}>
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';
